import { Button } from "@/components/ui/button.tsx";
import { FieldError } from "@/components/ui/field.tsx";
import { TextField } from "@/components/ui/text-field.tsx";
import { PlusIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { useI18nextInstance } from "@squide/i18next";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useCallback, useId, useState, type KeyboardEvent } from "react";
import { Input } from "react-aria-components/Input";
import { useTranslation } from "react-i18next";
import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { useSession } from "@packages/core-client/session/SessionContext.tsx";
import { useDebouncedValue } from "@packages/core-client/hooks/useDebouncedValue.ts";
import { segmentsI18NextInstanceKey } from "../../i18n/i18next.ts";
import { useSegmentsClient } from "../../api/segmentsQuery.ts";

const SUGGESTION_DEBOUNCE_MS = 300;

const FALLBACK_SUGGESTION_KEYS = ["analytics", "survey", "reviewCycle", "onboarding", "compensation"] as const;

const MAX_SUGGESTIONS = 5;

interface SuggestionButtonProps {
    tag: string;
    onAdd: (tag: string) => void;
}

function SuggestionButton({ tag, onAdd }: SuggestionButtonProps) {
    const handlePress = useCallback(() => onAdd(tag), [onAdd, tag]);

    return (
        <Button type="button" intent="outline" size="xs" onPress={handlePress}>
            <PlusIcon aria-hidden="true" />
            {tag}
        </Button>
    );
}

interface TagChipProps {
    tag: string;
    isReadOnly?: boolean;
    onRemove: (tag: string) => void;
}

// intent-ui-deviation: the design puts applied tags inside the same bordered box as the input, but Intent UI's
function TagChip({ tag, isReadOnly, onRemove }: TagChipProps) {
    const i18nextInstance = useI18nextInstance(segmentsI18NextInstanceKey);
    const { t } = useTranslation("segmentTags", { i18n: i18nextInstance });

    const handleRemove = useCallback(() => onRemove(tag), [onRemove, tag]);

    return (
        <span className={`bg-secondary text-secondary-fg inline-flex shrink-0 items-center gap-x-0.5 rounded-full py-0.5 ps-3 text-sm/5 ${isReadOnly ? "pe-3" : "pe-0.5"}`}>
            {tag}
            {!isReadOnly && (
                <Button type="button" intent="plain" size="sq-xs" aria-label={t("remove", { tag })} onPress={handleRemove}>
                    <XMarkIcon aria-hidden="true" />
                </Button>
            )}
        </span>
    );
}

export interface SegmentTagsProps {
    tags: string[];
    isReadOnly?: boolean;
    onChange: (tags: string[]) => void;
}

export function SegmentTags({ tags, isReadOnly, onChange }: SegmentTagsProps) {
    const i18nextInstance = useI18nextInstance(segmentsI18NextInstanceKey);
    const { t } = useTranslation("segmentTags", { i18n: i18nextInstance });
    const headingId = useId();
    const { queryUtils } = useSegmentsClient();
    const session = useSession();

    const [inputValue, setInputValue] = useState("");
    const [isDuplicate, setIsDuplicate] = useState(false);
    const debouncedInput = useDebouncedValue(inputValue, SUGGESTION_DEBOUNCE_MS);

    const { data: orgTags } = useQuery(
        createQueryOptions(queryUtils.searchSegmentTags.queryOptions({ input: { orgId: session?.organizationId ?? "", q: debouncedInput.trim() || undefined } }), {
            throwOnError: false,
            placeholderData: keepPreviousData,
            enabled: !isReadOnly
        })
    );

    const addTag = useCallback(
        (raw: string) => {
            const tag = raw.trim();

            if (!tag) {
                return;
            }

            if (tags.some(existing => existing.toLocaleLowerCase() === tag.toLocaleLowerCase())) {
                setIsDuplicate(true);

                return;
            }

            onChange([...tags, tag]);
            setInputValue("");
            setIsDuplicate(false);
        },
        [onChange, tags]
    );

    const handleInputChange = useCallback((value: string) => {
        setInputValue(value);
        setIsDuplicate(false);
    }, []);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === "Enter") {
                event.preventDefault();
                addTag(inputValue);
            }
        },
        [addTag, inputValue]
    );

    const handleRemove = useCallback(
        (tag: string) => {
            onChange(tags.filter(existing => existing !== tag));
            setIsDuplicate(false);
        },
        [onChange, tags]
    );

    const isApplied = useCallback((candidate: string) => tags.some(tag => tag.toLocaleLowerCase() === candidate.toLocaleLowerCase()), [tags]);

    const displayTags = Array.from(new Set(tags));

    const needle = debouncedInput.trim().toLocaleLowerCase();
    const orgSuggestions: string[] = [];
    const seenOrgTags = new Set<string>();

    for (const tag of orgTags?.items ?? []) {
        const key = tag.toLocaleLowerCase();

        if (!isApplied(tag) && !seenOrgTags.has(key)) {
            seenOrgTags.add(key);
            orgSuggestions.push(tag);
        }
    }

    const fallbackSuggestions = FALLBACK_SUGGESTION_KEYS.map(key => t(`suggestions.${key}`)).filter(tag => !isApplied(tag) && !seenOrgTags.has(tag.toLocaleLowerCase()) && (!needle || tag.toLocaleLowerCase().includes(needle)));
    const suggestions = [...orgSuggestions, ...fallbackSuggestions].slice(0, MAX_SUGGESTIONS);

    return (
        <section aria-labelledby={headingId} className="flex flex-col gap-2">
            <p id={headingId} className="text-fg text-sm font-medium">
                {t("heading")}
            </p>

            {isReadOnly ? (
                <div className="border-input flex min-h-10 flex-wrap items-center gap-1.5 rounded-lg border bg-(--control-bg,transparent) px-3 py-2 [--control-bg:var(--color-bg)]">
                    {displayTags.map(tag => (
                        <TagChip key={tag} tag={tag} isReadOnly onRemove={handleRemove} />
                    ))}
                </div>
            ) : (
                <TextField aria-label={t("heading")} value={inputValue} onChange={handleInputChange} onKeyDown={handleKeyDown} isInvalid={isDuplicate} className="flex flex-col">
                    <div className="border-input has-[input:enabled:hover]:border-muted-fg/30 has-[input:focus]:border-ring/70 has-[input:focus]:ring-ring/20 has-[input[aria-invalid=true]]:border-danger-subtle-fg/70 has-[input[aria-invalid=true]:focus]:border-danger-subtle-fg/70 has-[input[aria-invalid=true]:focus]:ring-danger-subtle-fg/20 flex flex-wrap items-center gap-1.5 rounded-lg border bg-(--control-bg,transparent) px-3 py-2 [--control-bg:var(--color-bg)] has-[input:focus]:ring-3">
                        {displayTags.map(tag => (
                            <TagChip key={tag} tag={tag} onRemove={handleRemove} />
                        ))}
                        <Input name="tags" placeholder={displayTags.length === 0 ? t("placeholder") : undefined} className="text-fg placeholder:text-muted-fg min-w-24 flex-1 bg-transparent text-sm/6 outline-hidden" />
                    </div>
                    <FieldError>{isDuplicate ? t("duplicate") : undefined}</FieldError>
                </TextField>
            )}

            {!isReadOnly && suggestions.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-muted-fg text-sm">{t("suggestedLabel")}</span>
                    {suggestions.map(suggestion => (
                        <SuggestionButton key={suggestion} tag={suggestion} onAdd={addTag} />
                    ))}
                </div>
            )}

            {!isReadOnly && <output className="sr-only">{t("suggestionsUpdated", { count: suggestions.length })}</output>}
        </section>
    );
}
